import { AxiosResponse } from 'axios';
import { PreciseRulesDTO } from '../forms/rule/deserializer';
import { buildPath } from '../lib/url';
import { updateDocumentAttachments } from './attachments';
import { backendClient as client } from './clients';
import {
    ActivityCalendarReadDTO,
    ActivityContextReadDTO,
    ActivityGridReadDTO,
    ActivityNimReadDTO,
    ActivityNimUpdateDTO,
    AddSignatureTaskUpdateDTO,
    AssignedSignatureDTO,
    CommentDTO,
    CommentUpdateDTO,
    ContractNimReadDTO,
    DocumentActionDTO,
    FavouriteActivityReadDTO,
    ListOfValuesReadDTO,
    RadiographySourceReadDTO,
} from './dto';
import { CreateOne, DeleteOne, GetManyPaged, GetManyUnpaged, GetOne, RequestMethod, UpdateOne } from './types';

export const ACTIVITY_PATH = 'activities_nim';
export const ACTIVITY_CR_PATH = 'change-request';
export const WATCHLIST_PATH = 'watchlist';

export const getActivity: GetOne<{ id: string }, ActivityNimReadDTO> = async ({ id, config }) =>
    client.get(buildPath([ACTIVITY_PATH, id]), config).then((response) => response.data);

export const getActivities: GetManyPaged<ActivityGridReadDTO> = async ({ filter, page = 0, size, sort, config } = {}) =>
    client
        .get(buildPath([ACTIVITY_PATH]), { ...config, params: { filter, page, size, sort } })
        .then((response) => response.data);

export const getCalendarActivities: GetManyPaged<ActivityCalendarReadDTO> = async ({
    filter,
    page = 0,
    size,
    sort,
    config,
} = {}) =>
    client
        .get(buildPath([ACTIVITY_PATH, 'calendar']), { ...config, params: { filter, page, size, sort } })
        .then((response) => response.data);

export const getActivityContext: RequestMethod<
    {
        activity: ActivityNimUpdateDTO & Pick<ActivityNimReadDTO, 'id'>;
        taskId: string;
        documentAction?: DocumentActionDTO | null;
    },
    PreciseRulesDTO<ActivityContextReadDTO>
> = async ({ activity, taskId, documentAction, config }) =>
    client
        .post(buildPath([ACTIVITY_PATH, 'context']), activity, {
            ...config,
            params: {
                id: activity.id,
                taskId,
                ...(documentAction != null && { documentAction }),
            },
        })
        .then((response: AxiosResponse) => response.data);

export const getActivityStatuses: GetManyUnpaged<{}, ListOfValuesReadDTO> = async ({ config } = {}) =>
    client.get(buildPath([ACTIVITY_PATH, 'statuses']), config).then((response: AxiosResponse) => response.data);

export const getActivityTypes: GetManyUnpaged<{ selectable?: boolean }, ListOfValuesReadDTO> = async ({
    config,
    selectable,
} = {}) =>
    client
        .get(buildPath([ACTIVITY_PATH, 'activity-types']), { ...config, params: { selectable } })
        .then((response: AxiosResponse) => response.data);

export const getActivityPriorities: GetManyUnpaged<{}, ListOfValuesReadDTO> = async ({ config } = {}) =>
    client
        .get(buildPath([ACTIVITY_PATH, 'activity-priorities']), config)
        .then((response: AxiosResponse) => response.data);

export const getInterventionPeriodTypes: GetManyUnpaged<{}, ListOfValuesReadDTO> = async ({ config } = {}) =>
    client
        .get(buildPath([ACTIVITY_PATH, 'intervention-periods']), config)
        .then((response: AxiosResponse) => response.data);

export const getActivityApprovalStatuses: GetManyUnpaged<{}, ListOfValuesReadDTO> = async ({ config } = {}) =>
    client
        .get(buildPath([ACTIVITY_PATH, 'approval-statuses']), config)
        .then((response: AxiosResponse) => response.data);

export const getActivityApprovalTypes: GetManyUnpaged<{}, ListOfValuesReadDTO> = async ({ config } = {}) =>
    client.get(buildPath([ACTIVITY_PATH, 'approval-types']), config).then((response: AxiosResponse) => response.data);

export const getActivityMeetingTypes: GetManyUnpaged<{}, ListOfValuesReadDTO> = async ({ config } = {}) =>
    client.get(buildPath([ACTIVITY_PATH, 'meeting-types']), config).then((response: AxiosResponse) => response.data);

export const getParticipantRoles: GetManyUnpaged<{ activityType: string | null }, ListOfValuesReadDTO> = async ({
    activityType,
    config,
}) =>
    client
        .get(buildPath([ACTIVITY_PATH, 'participant-roles', ...(activityType !== null ? [activityType] : [])]), config)
        .then((response: AxiosResponse) => response.data);

export const getCompensatoryMeasureTypes: GetManyUnpaged<{}, ListOfValuesReadDTO> = async ({ config } = {}) =>
    client
        .get(buildPath([ACTIVITY_PATH, 'compensatory-measure-types']), {
            ...config,
        })
        .then((response) => response.data);

export const getMaterialResources: GetManyUnpaged<{}, ListOfValuesReadDTO> = async ({ config } = {}) =>
    client
        .get(buildPath([ACTIVITY_PATH, 'material-resources']), {
            ...config,
        })
        .then((response) => response.data);

export const getConstraints: GetManyUnpaged<{}, ListOfValuesReadDTO> = async ({ config } = {}) =>
    client
        .get(buildPath([ACTIVITY_PATH, 'constraints']), {
            ...config,
        })
        .then((response) => response.data);

export const getOnSiteActivityStatuses: GetManyUnpaged<{}, ListOfValuesReadDTO> = async ({ config } = {}) =>
    client
        .get(buildPath([ACTIVITY_PATH, 'logbook', 'statuses']), {
            ...config,
        })
        .then((response) => response.data);

export const activityAttachmentsApi = updateDocumentAttachments(ACTIVITY_PATH);

export const updateActivity: UpdateOne<
    { id: number; taskId: string; data: ActivityNimUpdateDTO },
    ActivityNimReadDTO
> = async ({ id, taskId, data, config }) =>
    client
        .put(buildPath([ACTIVITY_PATH, id]), data, { ...config, params: { taskId } })
        .then((response: AxiosResponse) => response.data);

export const createActivity: CreateOne<{ data: ActivityNimUpdateDTO }, ActivityNimReadDTO> = async ({ data, config }) =>
    client.post(buildPath([ACTIVITY_PATH]), data, config).then((response: AxiosResponse) => response.data);

export const submitActivity: UpdateOne<{ id: number; data: ActivityNimUpdateDTO }, ActivityNimReadDTO> = async ({
    id,
    data,
    config,
}) =>
    client.put(buildPath([ACTIVITY_PATH, id, 'submit']), data, config).then((response: AxiosResponse) => response.data);

export const validateSubmitActivity: UpdateOne<{ id: number; data: ActivityNimUpdateDTO }> = async ({
    id,
    data,
    config,
}) => client.put(buildPath([ACTIVITY_PATH, id, 'validate']), data, config);

export const getActivityValidationRules: GetOne<
    { id: number | undefined },
    PreciseRulesDTO<ActivityContextReadDTO>
> = async ({ id, config }) =>
    client
        .get(buildPath([ACTIVITY_PATH, 'rules']), { ...config, params: { id } })
        .then((response: AxiosResponse) => response.data);

export const cancelActivity: RequestMethod<{ id: number; comment: CommentUpdateDTO }, any> = async ({
    id,
    comment,
    config,
}) =>
    client
        .post(buildPath([ACTIVITY_PATH, id, 'cancel']), comment, config)
        .then((response: AxiosResponse) => response.data);

export const closeActivity: RequestMethod<{ id: number }, any> = async ({ id, config }) =>
    client.post(buildPath([ACTIVITY_PATH, id, 'close']), config).then((response: AxiosResponse) => response.data);

export const interruptActivity: RequestMethod<{ id: number }, any> = async ({ id, config }) =>
    client.post(buildPath([ACTIVITY_PATH, id, 'interrupt']), config).then((response: AxiosResponse) => response.data);

export const resumeActivity: RequestMethod<{ id: number }, any> = async ({ id, config }) =>
    client.post(buildPath([ACTIVITY_PATH, id, 'resume']), config).then((response: AxiosResponse) => response.data);

export const eowActivity: RequestMethod<{ id: number; comment: CommentUpdateDTO }, any> = async ({
    id,
    comment,
    config,
}) =>
    client
        .post(buildPath([ACTIVITY_PATH, id, 'end-of-work']), comment, config)
        .then((response: AxiosResponse) => response.data);

export const addSignatureTask: RequestMethod<{ id: number; signature: AddSignatureTaskUpdateDTO }, any> = async ({
    id,
    signature,
    config,
}) =>
    client
        .post(buildPath([ACTIVITY_PATH, id, 'add-signature-task']), signature, config)
        .then((response: AxiosResponse) => response.data);

export const getContracts: GetManyPaged<ContractNimReadDTO> = async ({ filter, page, size, sort, config } = {}) =>
    client
        .get(buildPath([ACTIVITY_PATH, 'contracts']), { ...config, params: { filter, page, size, sort } })
        .then((response: AxiosResponse) => response.data);

export const getActivitySignatures: GetManyUnpaged<
    { id: number; data: ActivityNimUpdateDTO },
    AssignedSignatureDTO
> = async ({ id, data, config }) =>
    client
        .put(buildPath([ACTIVITY_PATH, id, 'signatures']), data, config)
        .then((response: AxiosResponse) => response.data);

export const getActivityPdf: GetOne<{ id: number; langCode: string }, Blob> = async ({ id, langCode, config }) =>
    client
        .get(buildPath([ACTIVITY_PATH, id, 'pdf']), { ...config, params: { langCode }, responseType: 'blob' })
        .then((response) => response.data);

export const getRadiographySources: GetManyUnpaged<{}, RadiographySourceReadDTO> = async ({ config } = {}) =>
    client
        .get(buildPath([ACTIVITY_PATH, 'radiography-sources']), config)
        .then((response: AxiosResponse) => response.data);

export const createActivityChangeRequest: CreateOne<
    { id: number; data: ActivityNimUpdateDTO },
    ActivityNimReadDTO
> = async ({ id, data, config }) =>
    client
        .post(buildPath([ACTIVITY_PATH, id, ACTIVITY_CR_PATH]), data, config)
        .then((response: AxiosResponse) => response.data);

export const getActivityChangeRequest: GetOne<{ id: number; changeRequestId: number }, ActivityNimReadDTO> = async ({
    id,
    changeRequestId,
    config,
}) =>
    client
        .get(buildPath([ACTIVITY_PATH, id, ACTIVITY_CR_PATH, changeRequestId]), config)
        .then((response: AxiosResponse) => response.data);

export const validateActivityChangeRequest: RequestMethod<
    { id: number; data: ActivityNimUpdateDTO },
    AssignedSignatureDTO[]
> = async ({ id, data, config }) =>
    client
        .put(buildPath([ACTIVITY_PATH, id, 'change-request', 'validate']), data, config)
        .then((response: AxiosResponse) => response.data);

export const cancelActivityChangeRequest: RequestMethod<
    { id: number; changeRequestId: number; comment: CommentUpdateDTO },
    void
> = async ({ id, changeRequestId, comment, config }) =>
    client
        .put(buildPath([ACTIVITY_PATH, id, 'change-request', changeRequestId, 'cancel']), comment, config)
        .then((response: AxiosResponse) => response.data);

export const addActivityComment: CreateOne<{ data: CommentUpdateDTO; id: string }, CommentDTO> = async ({
    data,
    id,
    config,
}) =>
    client
        .post(buildPath([ACTIVITY_PATH, id, 'add-comment']), data, config)
        .then((response: AxiosResponse) => response.data);

export const deleteActivityComment: DeleteOne<{ commentId: number }> = async ({ commentId, config }) =>
    client
        .delete(buildPath([ACTIVITY_PATH, 'delete-comment', commentId]), config)
        .then((response: AxiosResponse) => response.data);

export const getWatchlistActivities: GetManyPaged<ActivityGridReadDTO> = async ({
    filter,
    page = 0,
    size,
    sort,
    config,
} = {}) =>
    client
        .get(buildPath([ACTIVITY_PATH, WATCHLIST_PATH]), { ...config, params: { filter, page, size, sort } })
        .then((response) => response.data);

export const getActivityWatchers: GetManyUnpaged<{ id: number }, FavouriteActivityReadDTO> = async ({ id, config }) =>
    client.get(buildPath([ACTIVITY_PATH, id, WATCHLIST_PATH, 'getAll']), config).then((response) => response.data);

export const addActivityToTheWatchlist: RequestMethod<
    { id: number; personIds?: number[]; egroupsIds?: number[] },
    any
> = async ({ id, personIds, egroupsIds, config }) =>
    client
        .post(buildPath([ACTIVITY_PATH, id, WATCHLIST_PATH]), {}, { ...config, params: { personIds, egroupsIds } })
        .then((response: AxiosResponse) => response.data);

export const removeActivityFromTheWatchlist: RequestMethod<{ id: number }, any> = async ({ id, config }) =>
    client
        .delete(buildPath([ACTIVITY_PATH, id, WATCHLIST_PATH]), config)
        .then((response: AxiosResponse) => response.data);

export const getBinaryChoice: GetManyUnpaged<{}, ListOfValuesReadDTO> = async ({ config } = {}) =>
    client.get(buildPath([ACTIVITY_PATH, 'binary-choice']), config).then((response: AxiosResponse) => response.data);

export const getTernaryChoice: GetManyUnpaged<{}, ListOfValuesReadDTO> = async ({ config } = {}) =>
    client.get(buildPath([ACTIVITY_PATH, 'ternary-choice']), config).then((response: AxiosResponse) => response.data);

export const getModusOperandiDetailLevel: GetManyUnpaged<{}, ListOfValuesReadDTO> = async ({ config } = {}) =>
    client
        .get(buildPath([ACTIVITY_PATH, 'modus-operandi-detail-level']), config)
        .then((response: AxiosResponse) => response.data);
